import { useQueries, UseQueryResult } from 'react-query';
import { EventHasCalendarConflict } from 'types';
import { client } from 'utils/api-client';

export interface SessionConflictQuery {
  sessionId: string;
  startDate: string | null; // ISO
  endDate: string | null; // ISO
}

/**
 * Per-row staff calendar-conflict check for bulk event creation. Runs the same
 * `events/check-calendar-conflicts/` lookup the single-session drawer uses, once per
 * session row (via `useQueries`), and returns a `{ [sessionId]: hasConflict }` map so
 * each row can surface its own warning. Reuses the single-session logic (staff
 * availability + Google Calendar) — athlete conflicts are a separate follow-up.
 *
 * Shares a query key with `useCheckCalendarConflicts` whenever a row resolves to the
 * same start/end/staffs — which the first row always does, since it derives from the
 * same anchor session. Both hooks must therefore cache the same unwrapped payload
 * shape; do not change one to cache the raw AxiosResponse.
 */
export default function useCheckSessionsCalendarConflicts({
  sessions,
  staffs,
  enabled = true,
}: {
  sessions: SessionConflictQuery[];
  staffs: string[] | null;
  enabled?: boolean;
}): Record<string, boolean> {
  const hasStaffs = !!staffs && staffs.length > 0;

  const queries = sessions.map(session => ({
    session,
    isEnabled:
      enabled &&
      hasStaffs &&
      !!session.startDate &&
      !!session.endDate &&
      session.startDate !== session.endDate,
  }));

  const results = useQueries(
    queries.map(({ session, isEnabled }) => ({
      queryKey: ['events', 'check-calendar-conflicts', session.startDate, session.endDate, staffs],
      queryFn: async (): Promise<EventHasCalendarConflict> => {
        const params: Record<string, string> = {};
        if (hasStaffs) {
          params.staffs = (staffs as string[]).join(',');
        }
        if (session.startDate) {
          params.start_date = session.startDate;
        }
        if (session.endDate) {
          params.end_date = session.endDate;
        }
        const response = await client.get<EventHasCalendarConflict>('events/check-calendar-conflicts/', {
          params,
        });
        return response.data;
      },
      enabled: isEnabled,
      staleTime: 30 * 1000,
      cacheTime: 60 * 1000,
    })),
  ) as UseQueryResult<EventHasCalendarConflict>[];

  // A disabled query still reports whatever is cached under its key, and the first row
  // shares a key with the single-session anchor lookup. Only trust a row's result while
  // that row's own query is active — otherwise the anchor's answer leaks onto row 0 and
  // renders a duplicate per-row warning in single-session mode.
  return queries.reduce<Record<string, boolean>>((conflicts, { session, isEnabled }, index) => {
    conflicts[session.sessionId] = isEnabled ? results[index]?.data?.hasConflict ?? false : false;
    return conflicts;
  }, {});
}
